import React from 'react';
import { Card, CardHeader, CardContent, Box, Typography, IconButton, Avatar, ButtonBase, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import HistoryIcon from '@mui/icons-material/History';
import { useTranslation } from '../../../hooks/useTranslation';

export const WidgetRecent = ({ onRemove, isEditMode }) => {
    const t = useTranslation('Dashboard.json');
    const handleRemove = (e) => {
        e.stopPropagation();
        e.preventDefault();
        onRemove();
    };

    const recentApps = [
        { name: t('widget.recent.app.orders'), icon: <ShoppingCartIcon />, time: t('widget.recent.time.2min'), color: '#4CAF50' },
        { name: t('widget.recent.app.clients'), icon: <PeopleIcon />, time: t('widget.recent.time.15min'), color: '#2196F3' },
        { name: t('widget.recent.app.products'), icon: <InventoryIcon />, time: t('widget.recent.time.1h'), color: '#FF9800' },
        { name: t('widget.recent.app.dashboard'), icon: <DashboardIcon />, time: t('widget.recent.time.2h'), color: '#667eea' },
    ];

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '16px',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                bgcolor: 'background.paper',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
                    borderColor: 'primary.light'
                }
            }}
        >
            <CardHeader
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#2196F320', width: 32, height: 32 }}>
                            <HistoryIcon sx={{ fontSize: 18, color: '#2196F3' }} />
                        </Avatar>
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '15px', fontFamily: 'Poppins' }}>
                                {t('widget.recent.title')}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {t('widget.recent.subtitle')}
                            </Typography>
                        </Box>
                    </Box>
                }
                action={
                    isEditMode && (
                        <IconButton
                            className="widget-remove-btn"
                            onClick={handleRemove}
                            onMouseDown={(e) => e.stopPropagation()}
                            size="small"
                            sx={{
                                opacity: 0,
                                pointerEvents: 'none',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    bgcolor: 'error.light',
                                    color: 'error.main'
                                }
                            }}
                        >
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    )
                }
                sx={{
                    cursor: 'move',
                    pb: 1,
                    bgcolor: 'transparent',
                    '&:hover .widget-remove-btn': {
                        opacity: 1,
                        pointerEvents: 'all'
                    }
                }}
                className="widget-header"
            />
            <CardContent sx={{ flex: 1, pt: 0 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25
                    }}
                >
                    {recentApps.map((app, index) => (
                        <ButtonBase
                            key={index}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 1.5,
                                borderRadius: '12px',
                                transition: 'all 0.2s ease',
                                border: '1px solid transparent',
                                '&:hover': {
                                    bgcolor: 'rgba(0,0,0,0.02)',
                                    borderColor: app.color,
                                    boxShadow: `0 2px 8px ${app.color}15`
                                }
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 44,
                                    height: 44,
                                    bgcolor: `${app.color}15`,
                                    color: app.color
                                }}
                            >
                                {React.cloneElement(app.icon, { sx: { fontSize: 22 } })}
                            </Avatar>
                            <Box sx={{ flex: 1, textAlign: 'left' }}>
                                <Typography
                                    sx={{
                                        fontSize: 'clamp(13px, 1.3vw, 14px)',
                                        color: 'text.primary',
                                        fontWeight: 500,
                                        fontFamily: 'Poppins',
                                        mb: 0.25
                                    }}
                                >
                                    {app.name}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }}
                                >
                                    <AccessTimeIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                                    <Typography
                                        sx={{
                                            fontSize: 'clamp(11px, 1vw, 12px)',
                                            color: 'text.secondary',
                                            fontFamily: 'Poppins'
                                        }}
                                    >
                                        {app.time}
                                    </Typography>
                                </Box>
                            </Box>
                        </ButtonBase>
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
};
